import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatPrice } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { DateTimeRangePicker } from '../components/ui/date-time-range-picker';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Car as CarIcon, Printer, Eye, Search, Edit } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import CarInvoice, { saleToInvoiceCar } from '../components/CarInvoice';
import type { Sale } from '../types/car';
import { api } from '../lib/api';

type DateRange = { from: Date | undefined; to?: Date | undefined };

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  totalSalesUSD: number;
  totalSalesIQD: number;
}

interface SalesResponse {
  sales: Sale[];
  pagination: PaginationData;
}

export default function SellCarHistory() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [sales, setSales] = useState<Sale[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
  const [models, setModels] = useState<{ id: number; name: string; brandId: number }[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedModel, setSelectedModel] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/api/brands');
        setBrands(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchSales();
  }, [dateRange, selectedBrand, selectedModel, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    if (selectedBrand && selectedBrand !== 'all') {
      api.get(`/api/models/brand/${selectedBrand}`).then((data) => setModels(Array.isArray(data) ? data : [])).catch(() => setModels([]));
    } else {
      setModels([]);
      setSelectedModel('all');
    }
  }, [selectedBrand]);

  const fetchSales = async () => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange?.from) params.append('fromDate', dateRange.from.toISOString());
      if (dateRange?.to) params.append('toDate', dateRange.to.toISOString());
      if (selectedBrand !== 'all') params.append('brandId', selectedBrand);
      if (selectedModel !== 'all') params.append('modelId', selectedModel);
      if (debouncedSearchTerm.trim()) params.append('search', debouncedSearchTerm.trim());
      params.append('page', String(currentPage));
      params.append('limit', String(itemsPerPage));

      const data: SalesResponse = await api.get(`/api/sales?${params.toString()}`);
      setSales(data.sales || []);
      setPagination(data.pagination || null);
    } catch (e) {
      console.error(e);
    } finally {
      setTableLoading(false);
    }
  };

  const clearFilters = () => {
    setDateRange(undefined);
    setSelectedBrand('all');
    setSelectedModel('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const totalPaid = (sale: Sale) => (sale.payments || []).reduce((sum, p) => sum + p.amount, 0);
  const remaining = (sale: Sale) => sale.salePrice - totalPaid(sale);

  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: selectedSale ? `Sale Invoice - ${selectedSale.warehouseCar.brand.name} ${selectedSale.warehouseCar.model.name}` : 'Sale Invoice',
    pageStyle: `@page { size: A4; margin: 0; } @media print { body { -webkit-print-color-adjust: exact; } }`,
  });

  const handlePrintInvoice = (sale: Sale) => {
    setSelectedSale(sale);
    setShowInvoice(true);
    setTimeout(() => {
      handlePrint();
      setTimeout(() => setShowInvoice(false), 1000);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('sellCarHistory.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('sellCarHistory.subtitle')}</p>
          </div>
          <Link to="/sell-car">
            <Button variant="outline" size="sm" className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('sellCarHistory.backToSell')}
            </Button>
          </Link>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>{t('sellCarHistory.salesHistory')}</CardTitle>
            <CardDescription>{t('sellCarHistory.salesDescription')}</CardDescription>
            <div className="flex flex-wrap gap-4 mt-4">
              <div className="flex-1 min-w-[200px]">
                <Label>{t('sellCarHistory.search')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input className="pl-10" placeholder={t('sellCarHistory.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>
              <div className="w-80">
                <Label>{t('sellCarHistory.dateRange')}</Label>
                <DateTimeRangePicker value={dateRange} onValueChange={setDateRange} className="w-full" />
              </div>
              <div className="w-48">
                <Label>{t('sellCarHistory.brand')}</Label>
                <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                  <SelectTrigger><SelectValue placeholder={t('sellCarHistory.allBrands')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('sellCarHistory.allBrands')}</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label>{t('sellCarHistory.model')}</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel} disabled={selectedBrand === 'all'}>
                  <SelectTrigger><SelectValue placeholder={selectedBrand !== 'all' ? t('sellCarHistory.allModels') : t('sellCarHistory.selectBrandFirst')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('sellCarHistory.allModels')}</SelectItem>
                    {models.map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={clearFilters} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">{t('sellCarHistory.clearFilters')}</Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2 items-center">
              <Badge variant="secondary">{pagination?.totalCount ?? 0} {t('sellCarHistory.cars')}</Badge>
              <Badge variant="secondary" className="font-medium text-emerald-700 bg-emerald-50 border border-emerald-200">
                {t('sellCarHistory.totalUSD')}: {formatPrice(pagination?.totalSalesUSD ?? 0, 'USD')}
              </Badge>
              <Badge variant="secondary" className="font-medium text-blue-700 bg-blue-50 border border-blue-200">
                {t('sellCarHistory.totalIQD')}: {formatPrice(pagination?.totalSalesIQD ?? 0, 'IQD')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {sales.length === 0 && !tableLoading ? (
              <div className="text-center py-12">
                <CarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('sellCarHistory.noSales')}</p>
                <Link to="/sell-car"><Button className="mt-4">{t('sellCarHistory.sellFirstCar')}</Button></Link>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.carId')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.vehicle')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.plateNumber')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.year')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.color')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.salePrice')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.seller')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.buyer')}</TableHead>
                      <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.soldDate')}</TableHead>
                      <TableHead className="text-right text-slate-600 font-medium text-sm">{t('sellCarHistory.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 10 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}
                        </TableRow>
                      ))
                    ) : (
                      sales.map((sale) => (
                        <TableRow key={sale.id} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm text-slate-700">#{sale.id}</TableCell>
                          <TableCell>
                            <span dir="ltr">{sale.warehouseCar.brand.name} {sale.warehouseCar.model.name}</span>
                          </TableCell>
                          <TableCell>{sale.warehouseCar.plateNumber || '-'}</TableCell>
                          <TableCell>{sale.warehouseCar.year}</TableCell>
                          <TableCell>{sale.warehouseCar.color}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-semibold text-emerald-600">{formatPrice(sale.salePrice, sale.currency ?? 'USD')}</div>
                              <div className="text-xs text-blue-600">{formatPrice(totalPaid(sale), sale.currency ?? 'USD')}</div>
                              {remaining(sale) > 0 && <div className="text-xs text-red-600">{formatPrice(remaining(sale), sale.currency ?? 'USD')}</div>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{sale.sellerName || 'N/A'}</div>
                              <div className="text-slate-500">{sale.sellerContact || 'N/A'}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{sale.buyer?.name ?? 'N/A'}</div>
                              <div className="text-gray-500">{sale.buyer?.contact ?? ''}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">{formatDate(sale.soldAt)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => navigate(`/sell-car-history/${sale.id}`)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => navigate(`/edit-car/${sale.id}`)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => handlePrintInvoice(sale)}>
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                {pagination && pagination.totalPages > 1 && !tableLoading && (
                  <div className="flex justify-between mt-6">
                    <span className="text-sm text-slate-500">
                      {t('sellCarHistory.showing')} {((pagination.currentPage - 1) * pagination.limit) + 1} {t('sellCarHistory.to')} {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} {t('sellCarHistory.of')} {pagination.totalCount} {t('sellCarHistory.results')}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={!pagination.hasPrevPage} onClick={() => setCurrentPage((p) => p - 1)} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">{t('sellCarHistory.previous')}</Button>
                      <Button variant="outline" size="sm" disabled={!pagination.hasNextPage} onClick={() => setCurrentPage((p) => p + 1)} className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">{t('sellCarHistory.next')}</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {showInvoice && selectedSale && (
        <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
          <CarInvoice ref={invoiceRef} car={saleToInvoiceCar(selectedSale)} payments={selectedSale.payments || []} isReprint />
        </div>
      )}
    </div>
  );
}
